import { useCallback, useState } from "react";

import { Table, Pager, TableDataSource, Modal, Buttons, Button, DataId, Paged } from "capitalrx-components-2.0/lib/general";
import { PricingLevelsTypes } from "@/types";

import { useHistory } from "../../hooks";
import { formatStringArray } from "../../utils";
import { Regular } from "capitalrx-components-2.0/lib/typography";

import styles from "./PricingLevelListTable.module.scss";

type Props = {
  dataSource: TableDataSource<PricingLevelsTypes.PricingLevelAPIResult> & Paged;
};

/**
 * Pricing Level List Results Table.
 */
export const PricingLevelListTable = ({ dataSource }: Props): JSX.Element => {
  const [navigate] = useHistory();

  const [modalRecordId, setModalRecordId] = useState<DataId>(null);

  const [notesText, setNotesText] = useState<string>("");
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);

  const [coverageStrategyIdModalList, setCoverageStrategyIdModalList] = useState<string[]>([]);
  const [showCoverageStrategyIdModal, setShowCoverageStrategyIdModal] = useState<boolean>(false);

  const handleRowClick = useCallback((id: DataId) => {
    navigate(`/pricing-levels/${id}/edit`);
  }, []);

  const onNotesShowMoreClick = useCallback((id: DataId, noteText: string): void => {
    setModalRecordId(id);
    setNotesText(noteText);
    setShowNotesModal(true);
  }, [setNotesText, setShowNotesModal]);

  const onCoverageStrategyIdShowMoreClick = useCallback((id: DataId, csids: string[]): void => {
    setModalRecordId(id);
    setCoverageStrategyIdModalList(csids);
    setShowCoverageStrategyIdModal(true);
  }, [setCoverageStrategyIdModalList, setShowCoverageStrategyIdModal]);

  const renderNotesColumn = (note: string, id: DataId) => {
    if (!note) {
      return "--";
    }

    if (note.length <= 50) {
      return note;
    }

    const truncatedNote = note.slice(0, 50) + "... ";

    return (
      <div onClick={(e) => e.stopPropagation()}>
        {truncatedNote}
        <Button
          className={styles.ShowMoreLink}
          appearance={Buttons.Functional.Tertiary.Small.Unpadded}
          onClick={() => onNotesShowMoreClick(id, note)}
        >
          <Regular>show more</Regular>
        </Button>
      </div>
    );
  };

  const renderCoverageStrategyIdColumn = (csids: string[], id: DataId) => {
    if (csids.length <= 3) {
      return csids.join(", ") || "--";
    }

    const truncatedValues = csids.slice(0, 3).join(", ") + "... ";

    return (
      <div onClick={(e) => e.stopPropagation()}>
        {truncatedValues}
        <Button
          className={styles.ShowMoreLink}
          appearance={Buttons.Functional.Tertiary.Small.Unpadded}
          onClick={() => onCoverageStrategyIdShowMoreClick(id, csids)}
        >
          <Regular>show more</Regular>
        </Button>
      </div>
    );
  };

  const notesModal = (
    <Modal.Confirm
      visible={showNotesModal}
      onClose={() => setShowNotesModal(false)}
      title={`Pricing Level #${modalRecordId}: Notes`}
      confirmContent="Close"
    >
      <div className={styles.NotesModalText}>{notesText}</div>
    </Modal.Confirm>
  );

  const coverageStrategyIdModal = (
    <Modal.Confirm
      visible={showCoverageStrategyIdModal}
      onClose={() => setShowCoverageStrategyIdModal(false)}
      title={`Pricing Level #${modalRecordId}: Coverage Strategy IDs`}
      confirmContent="Close"
    >
      <ul>
        {coverageStrategyIdModalList.map((csid: string) => {
          return <li key={csid}>{csid}</li>;
        })}
      </ul>
    </Modal.Confirm>
  );

  return (
    <>
      {notesModal}
      {coverageStrategyIdModal}
      <Table
        dataSource={dataSource}
        testId="pricing-level-list-table"
        footer={<Pager dataSource={dataSource} />}
        onRowClick={handleRowClick}
      >
        <Table.Text name="pricing_level.id" title="ID" sortable="id" />
        <Table.Date name="pricing_level.created_at" title="Created" sortable="created_at" format="LL/dd/yyyy"/>
        <Table.Text name="pricing_level.client_id" title="Client ID" sortable="client_id" />
        <Table.Text name="pricing_level.external_group_id" title="External Group ID" sortable="external_group_id" />
        <Table.Text name="pricing_level.external_account_id" title="External Account ID" sortable="external_account_id" />
        <Table.Text name="pricing_level.level" title="Level" sortable="level" />
        <Table.Text name="pricing_level.order" title="Order" sortable="order" />
        <Table.Custom name="pricing_level.lines_of_business" sortable="lines_of_business" title="Line of Business">
          {(values:string[]) => {
            return formatStringArray(values);
          }}
        </Table.Custom>
        <Table.Custom name="pricing_level.notes" title="Notes" sortable="notes">
          {(value: string, id: DataId) => renderNotesColumn(value, id)}
        </Table.Custom>
        <Table.Custom name="pricing_level.coverage_strategy_ids" sortable="coverage_strategy_ids" title="Coverage Strategy ID(s)">
          {(values: string[], id: DataId) => renderCoverageStrategyIdColumn(values, id)}
        </Table.Custom>
        <Table.Custom name="pricing_level.external_network_ids" sortable="external_network_ids" title="Network">
          {(value) => {
            return `${value.toString()}`;
          }}
        </Table.Custom>
        <Table.Custom name="pricing_level.agreement_types" sortable="agreement_types" title="Provider Agreement Type">
          {(values:string[]) => {
            return formatStringArray(values);
          }}
        </Table.Custom>
        <Table.Custom name="pricing_level.agreement_channels" sortable="agreement_channels" title="Provider Agreement Channel">
          {(values:string[]) => {
            return formatStringArray(values);
          }}
        </Table.Custom>
        <Table.Date
          name="pricing_level.minimum_claim_submission_date"
          title="Start Date"
          format="LL/dd/yyyy"
          sortable="minimum_claim_submission_date"
        />
        <Table.Date
          name="pricing_level.maximum_claim_submission_date"
          title="End Date"
          format="LL/dd/yyyy"
          sortable="maximum_claim_submission_date"
        />
      </Table>
    </>
  );
};
